import {
  CellValues,
  ColumnHeading,
  EntityFilterControlsConfig,
  PaginatedDataTable,
  TableCellStylesFn,
  useIsDesktop,
  useVisibleHeadings,
} from '@lbcamden/lib-react';
import { formatDMY, formatDMYWithTime } from '@lbcamden/shared-utils';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { AddNewButton } from '~/client/components/common/Button/AddNewButton';
import { SectionContainer } from '~/client/components/common/SectionContainer';
import { DirectoryUserInfo } from '~/client/components/shared/DirectoryUserInfo';
import { visitOutcomeDisplayValues } from '~/client/config/display-values/visits';
import { useVisitList } from '~/client/hooks/storeHooks';
import { toVisitCreatePage, toVisitPage } from '~/client/utils/navigation';
import { PropertyVisit } from '~/common/types/visits';
import { selectOptionsFromDisplayValues } from '~/common/utils/data';

type Props = {
  propertyId: number;
};

type ColumnId = StringKey<PropertyVisit>;

const getHeadings = (isDesktop: boolean): ColumnHeading<ColumnId>[] => [
  { id: 'timestamp', content: isDesktop ? 'Date/Time' : 'Date' },
  { id: 'outcome', content: 'Outcome' },
  { id: 'officerName', content: 'Officer' },
];

const filterConfigs: EntityFilterControlsConfig<PropertyVisit> = [
  {
    type: 'multi-select',
    key: 'outcome',
    label: 'Outcome',
    options: selectOptionsFromDisplayValues(visitOutcomeDisplayValues),
  },
];

const getMapVisitToRow =
  (isDesktop: boolean) =>
  (visit: PropertyVisit): CellValues<ColumnId> => ({
    _rowId: visit.visitId,
    timestamp: (isDesktop ? formatDMYWithTime : formatDMY)(visit.timestamp),
    outcome: visitOutcomeDisplayValues[visit.outcome],
    officerName: <DirectoryUserInfo camdenAccountName={visit.officerCamdenAccountName} />,
  });

const cellStyles: TableCellStylesFn = (columnId) => [
  { width: columnId === 'timestamp' ? 80 : 150 },
];

export const Visits: FunctionComponent<Props> = ({ propertyId }) => {
  const navigate = useNavigate();

  const isDesktop = useIsDesktop();

  const visits = useVisitList(propertyId);

  const headings = useMemo(() => getHeadings(isDesktop), [isDesktop]);
  const mapVisitToRow = useMemo(() => getMapVisitToRow(isDesktop), [isDesktop]);

  const visibleHeadings = useVisibleHeadings(headings, {
    xs: headings.slice(0, 2),
    sm: headings.slice(0, 2),
    md: headings.slice(0, 4),
  });

  const onAddNew = () => navigate(toVisitCreatePage(propertyId));

  const onClickRow = useCallback(
    (visit: PropertyVisit) => navigate(toVisitPage(visit.propertyId, visit.visitId)),
    [],
  );

  return (
    <SectionContainer
      title="Visit log"
      headerActions={<AddNewButton label="Log new visit" onClick={onAddNew} />}
    >
      <PaginatedDataTable
        cellStyles={cellStyles}
        headings={visibleHeadings}
        filterConfigs={filterConfigs}
        items={visits}
        defaultSort={{ field: 'timestamp', direction: 'desc' }}
        mapItemToRow={mapVisitToRow}
        onClickRow={onClickRow}
      />
    </SectionContainer>
  );
};
